import { useMemo, useState } from "react";
import { HelpCircle } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Textarea } from "~/components/ui/textarea";
import { parseToolBody } from "./parseToolBody";
import type { ToolCallEntry } from "./useTurns";

interface DeferredToolFallbackCardProps {
  entry: ToolCallEntry;
  // Resume the paused run with the user's response. Omitted in read-only
  // views (shared / admin), where the card renders but cannot be answered.
  // Rejects if the resume fails, letting the card re-enable Submit for retry.
  onAnswer?: (toolCallId: string, answer: string) => void | Promise<void>;
}

// Generic answer box for a HITL tool (HITL_TOOL_NAMES) with no bespoke card in
// HITL_TOOL_RENDERERS, so the backend can ship a HITL tool ahead of its card
// without bricking the run. A deferred tool-return is just a string on the
// wire, so free text is a valid answer — we lose the rich UI but never the
// ability to continue.
export const DeferredToolFallbackCard = ({ entry, onAnswer }: DeferredToolFallbackCardProps) => {
  const [text, setText] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const readOnly = onAnswer === undefined;

  // Show the raw arguments so the user has the context the missing card would
  // otherwise present. Pretty-print JSON; fall back to the verbatim string.
  const argsPreview = useMemo(() => {
    const view = parseToolBody(entry.args);
    if (view.kind === "json") return JSON.stringify(view.value, null, 2);
    if (view.kind === "instructions") return view.text;
    if (view.kind === "text") return view.text;
    return null;
  }, [entry.args]);

  const handleSubmit = async () => {
    const answer = text.trim();
    if (!answer || submitted || !onAnswer) return;
    setSubmitted(true);
    try {
      await onAnswer(entry.id, answer);
    } catch {
      // Resume failed: unlock so the user can retry. The caller has already
      // rolled back the optimistic tool message.
      setSubmitted(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-background p-4 shadow-sm">
      <div className="flex items-center gap-2">
        <HelpCircle className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="text-sm font-medium text-foreground">
          The agent is waiting on <code className="text-xs">{entry.name}</code>
        </span>
      </div>
      <p className="text-xs text-muted-foreground">
        This tool has no custom form yet. Reply in plain text to continue.
      </p>
      {argsPreview && (
        <pre className="max-h-40 overflow-auto rounded-md bg-muted/50 p-2 text-xs text-muted-foreground">
          {argsPreview}
        </pre>
      )}
      {readOnly ? (
        <span className="self-end text-xs italic text-muted-foreground">Awaiting response</span>
      ) : (
        <>
          <Textarea
            autoFocus
            value={text}
            disabled={submitted}
            placeholder="Type your response…"
            onChange={(e) => setText(e.target.value)}
            className="min-h-20 text-sm"
          />
          <div className="flex justify-end">
            <Button size="sm" onClick={handleSubmit} disabled={!text.trim() || submitted}>
              {submitted ? "Submitting…" : "Submit"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
